import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faBox,
  faAlignLeft,
  faTags,
  faBarcode,
  faPercent,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * Small presentational row for displaying product information.
 */
const InfoRow = ({ icon, label, value, highlight = false }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  const displayValue = value || emDash;
  return (
    <div
      className="flex items-start justify-between rounded-xl border px-4 py-3"
      style={{
        borderColor: UI_COLORS.BORDERS.LIGHT,
        backgroundColor: highlight
          ? colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)
          : UI_COLORS.BACKGROUNDS.CARD,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.3),
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          <FontAwesomeIcon icon={icon} className="text-base" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {label}
          </p>
          <p className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Format price with rupee symbol and proper formatting
/**
 * Format GST rate percentage
 */
const formatGstRate = (rate, emDash) => {
  if (!rate && rate !== 0) return emDash;
  const numRate = parseFloat(rate);
  if (isNaN(numRate)) return emDash;
  return `${numRate}%`;
};

/**
 * Modal that displays read-only product information.
 */
const ProductDetails = ({ isOpen, onClose, product, loading = false }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const strings = t('productCatalogue.view', { returnObjects: true });
  const emDash = t('common.symbols.emDash');

  const productName = product?.product_name || strings.header.fallbackTitle;
  const description = product?.description || emDash;
  const categoryName = product?.category_name || product?.product_category?.name || emDash;

  const hsnCodeValue = product?.hsn_code_value || product?.hsn_code?.code || emDash;
  const hsnDescription = product?.hsn_code?.description || product?.hsn_code?.commodity_description || emDash;
  const gstRateValue = formatGstRate(product?.gst_rate || product?.hsn_code?.gst_rate, emDash);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.product-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .product-view-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="product-view-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {productName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              ></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.loading}
              </p>
            </div>
          ) : !product ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon
                icon={faBox}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.TEXT.MUTED }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.empty.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.empty.message}
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faBox} label={strings.fields.productName} value={productName} highlight />
                <InfoRow icon={faTags} label={strings.fields.category} value={categoryName} />
              </div>

              {description !== emDash && (
                <div
                  className="rounded-xl border px-4 py-3"
                  style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-lg"
                      style={{
                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.3),
                        color: UI_COLORS.TEXT.PRIMARY,
                      }}
                    >
                      <FontAwesomeIcon icon={faAlignLeft} className="text-base" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                      {strings.fields.description}
                    </p>
                  </div>
                  <p className="text-base" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {description}
                  </p>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faBarcode} label={strings.fields.hsnCode} value={hsnCodeValue} />
                <InfoRow icon={faPercent} label={strings.fields.gstRate} value={gstRateValue} />
              </div>

              {hsnDescription !== emDash && (
                <div
                  className="rounded-xl border px-4 py-3"
                  style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-lg"
                      style={{
                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.3),
                        color: UI_COLORS.TEXT.PRIMARY,
                      }}
                    >
                      <FontAwesomeIcon icon={faBarcode} className="text-base" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                      {strings.fields.hsnDescription}
                    </p>
                  </div>
                  <p className="text-base" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {hsnDescription}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-3 border-t px-6 py-4"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {strings.buttons.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
